import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'semantic-ui-react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import ChangeHistory, { KindToggles, isEntryVisible, prepareEntries } from './ChangeHistory';

const OPEN_PATH = {
	Events: (id) => `/editor?id=${id}&tab=History`,
	EventTemplates: (id) => `/template?id=${id}&tab=History`,
	Groups: (id) => `/group?id=${id}`,
	Members: (id) => `/profile?id=${id}`,
};

/**
 * Every saved change to one record (History tabs): when, who, what, version.
 * `only`: the kinds that make sense here (templates / groups: edits only — then no toggles are shown).
 * `showTarget`: rows can be about other records (a profile's history includes texts / replies on events).
 * `version` changing (after a save) reloads it.
 */
export default function RecordHistory({ collection, id, version, only, showTarget }) {
	const navigate = useNavigate();
	const [kinds, setKinds] = useState(only ?? ['edit', 'notification', 'confirmation']);
	const [entries, setEntries] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const request = useRef(0);

	const load = useCallback(async () => {
		const reqId = ++request.current;
		setLoading(true);
		setError(null);
		try {
			const data = await api.getHistory(collection, id);
			if (reqId === request.current) setEntries(data.entries ?? []);
		}
		catch (ex) {
			if (reqId === request.current) setError(ex);
		}
		finally {
			if (reqId === request.current) setLoading(false);
		}
	}, [collection, id]);

	useEffect(() => { if (id) load(); }, [id, load]);

	// history is written by a trigger right after a save: give it a moment, then reload
	const firstVersion = useRef(version);
	useEffect(() => {
		if (version === firstVersion.current) return;
		const timers = [setTimeout(load, 2500), setTimeout(load, 8000)];
		return () => timers.forEach(clearTimeout);
	}, [version, load]);

	const counts = {};
	const rows = prepareEntries(entries, { acrossRecords: !!showTarget });
	['edit', 'notification', 'confirmation'].forEach(k => { counts[k] = rows.filter(e => isEntryVisible(e, [k])).length; });
	const singleKind = only?.length === 1;

	return (
		<div>
			<div className='ch-panel-tools'>
				{singleKind ? <span /> : <KindToggles kinds={kinds} onChange={setKinds} counts={loading ? undefined : counts} only={only} />}
				<Button basic compact size='small' icon='refresh' content='Refresh' onClick={load} disabled={loading} />
			</div>
			<ChangeHistory
				entries={entries}
				kinds={kinds}
				loading={loading && entries.length === 0}
				error={error}
				showTarget={showTarget}
				onOpenTarget={showTarget ? (e) => { const path = OPEN_PATH[e.collection]?.(e.docId); if (path && !(e.collection === collection && e.docId === id)) navigate(path); } : undefined}
				emptyText={kinds.length === 0
					? 'Turn on Edits, Notifications or Replies to see changes.'
					: 'No changes recorded yet. Changes are recorded from now on, each time this is saved.'}
			/>
		</div>
	);
}
